/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { defaultToEmptyString } from '../../../assets/ts/formatting/formatting'
import { decodeHTML } from '../../../assets/ts/general/general'
import PageOverlayBusy from '../../../components/page-overlay/page-overlay-busy'
import useInquiries from '../../../hooks/inquiry/use-inquiries'
import PageHeader from '../../../layouts/header/page-header'
import { PreviousPageContext } from '../../../store/previous-page-provider'
import { RoleContext } from '../../../store/role-context-provider'
import { INQUIRY_ACTION_STRINGS } from '../../../utils/constants/global-constants'
import { PersonDetails } from '../../../utils/constants/global-interfaces'
import ActionButtonsContainer from '../action-buttons-container/action-buttons-container'
import Checklist from '../checklist/checklist'
import Details from '../details/details'
import Discussions from '../discussions/discussions'
import DetailViewFiles from '../files/detail-view-files'
import ProgressBar from '../progress-steps/progress-bar'
import TeamMembers from '../team-members/team-members'
import './detailview.css'

const DetailView = (props: any) => {
  const { guid } = useParams()
  const { inquiries, inquiriesAreLoading, refetch } = useInquiries({ inquiryGuid: guid })
  const currentInquiry = inquiries[0]

  const roleContext = useContext(RoleContext)
  const {
    isAFRCOwner,
    isANGOwner,
    isCPOwner,
    isLeadership,
    isMAJCOMOwner,
    isMPFOwner,
    mpfSection,
  } = useContext(RoleContext)
  const canSeeEverything = [
    isAFRCOwner,
    isANGOwner,
    isCPOwner,
    isLeadership,
    isMAJCOMOwner,
    isMPFOwner,
  ]
  const { currentPage, setCurrentPage, setPreviousPage } = useContext(PreviousPageContext)
  const [previousListPage , setPreviousListPage]  = useState('')
  useEffect(() => {
    // This effect runs whenever the location changes (when the user navigates to a different route)
    if (location && currentPage && currentPage !== location.pathname) {
      setPreviousListPage(currentPage)
      // User has navigated to a new page, so update the previous page
      setPreviousPage(currentPage) // Store the previous page
      setCurrentPage(location.pathname) // Update the current page
    }
  }, [location, currentPage]) // Runs whenever location or currentPage changes
  function getResponseStatus(currentInquiry, mpfSection, canSeeEverything) {
    const isResponseRequired =
      currentInquiry?.ResponseRequired &&
      currentInquiry['Section/MetadataValue'] !== currentInquiry['PreviousSection/MetadataValue'] &&
      (mpfSection.includes(currentInquiry['Section/MetadataValue']) ||
        canSeeEverything.includes(true))

    const isResponsePending =
      currentInquiry?.ResponseRequired &&
      ((currentInquiry['Section/MetadataValue'] !==
        currentInquiry['PreviousSection/MetadataValue'] &&
        mpfSection.includes(currentInquiry['PreviousSection/MetadataValue'])) ||
        canSeeEverything.includes(true))

    if (isResponseRequired && !isResponsePending) {
      return 'RR'
    } else if (isResponsePending && !isResponseRequired) return 'RP'
    else if (isResponseRequired && isResponsePending) {
      return 'Both'
    } else {
      return 'Neither'
    }
  }

  const [currentUserRole, setCurrentUserRole] = useState<string>('')
  const [currentAction, setCurrentAction] = useState<string>('')

  const [refresh, setRefresh] = useState(false)
  const [isLoading, setIsLoading] = useState(inquiriesAreLoading)

  const [allFiles, setAllFiles] = useState<any>([])

  const description = useMemo(() => {
    return decodeHTML(currentInquiry?.Description)
  }, [currentInquiry])

  const detailsItems = useMemo(
    () => [
      {
        link: '#',
        subtitle: `${currentInquiry?.ServicingBy}`,
        title: 'Servicing By',
      },
      {
        link: '#',
        subtitle:
          currentInquiry?.ItemCreatedBy?.userID ===
          (currentInquiry?.Customer as PersonDetails)?.userID
            ? 'Self'
            : 'Other',
        title: 'Request For',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.['CustomerStatus']}`,
        title: 'Status',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.DoDNumber}`,
        title: 'DoD ID Number',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.Rank}`,
        title: 'Rank',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.DutyEmail}`,
        title: 'Duty Email',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.DutyPhone}`,
        title: 'Duty Phone',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.PASCODE}`,
        title: 'PASCODE',
      },
      {
        link: '#',
        subtitle: `${decodeHTML(currentInquiry?.Organization)}`,
        title: 'Organization',
      },
      {
        link: '#',
        subtitle: `${decodeHTML(currentInquiry?.Installation)}`,
        title: 'Assigned Location',
      },
    ],
    [currentInquiry]
  )
  const inquiryDetailsItems = useMemo(
    () => [
      {
        link: '#',
        subtitle: `${currentInquiry?.['Section/MetadataValue']}`,
        title: 'MPF Section',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.ServicingBy}`,
        title: 'Servicing By',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.['Category/MetadataValue']}`,
        title: 'Category',
      },
      {
        link: '#',
        subtitle: `${currentInquiry?.['SubCategory/MetadataValue'] ?? '-'}`,
        title: 'Sub Category',
      },
    ],
    [currentInquiry]
  )
  const getSelectedFiles = (files: any, allFiles: any) => {
    setAllFiles(allFiles)
  }

  const hideButton = (action: string) => {
    if (
      action.toUpperCase() === INQUIRY_ACTION_STRINGS.Resolve.toUpperCase() ||
      action.toUpperCase() === INQUIRY_ACTION_STRINGS.Closed.toUpperCase() ||
      action.toUpperCase() === INQUIRY_ACTION_STRINGS.Cancel.toUpperCase()
    ) {
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    if (refresh) {
      // Logic to re-fetch data or re-render the component
      refetch()
      setRefresh(false)
    }
  }, [refresh])

  return (
    <>
      {isLoading && <PageOverlayBusy />}
      <PageHeader
        badge={
          currentUserRole !== 'Customer' &&
          getResponseStatus(currentInquiry, mpfSection, canSeeEverything)
        }
        extras={
          <ActionButtonsContainer
            actionType='Inquiry'
            currentUserRoleProps={{ currentUserRole, setCurrentUserRole }}
            data={currentInquiry}
            isMenusExpanded={props?.isMenusExpanded}
            pageType='inquirydetailview'
            refresh={refresh}
            setRefresh={setRefresh}
          />
        }
        name={`${defaultToEmptyString(currentInquiry?.InquiryID)} - ${defaultToEmptyString(
          decodeHTML(currentInquiry?.Subject)
        )}`}
        titleclass='color-primary'
      />

      <div className='pb-2 w-100'>
        <div className='container'>
          <div className='row'>
            <div className='col-sm-12 col-md-3 col-lg-3 mb-3'>
              <div className='shadow card w-100 h-100'>
                <ProgressBar
                  ItemGUID={guid}
                  inquiryData={currentInquiry}
                  refresh={refresh}
                  setCurrentAction={setCurrentAction}
                  setIsLoading={setIsLoading}
                  setRefresh={setRefresh}
                />
              </div>
            </div>
            <div className='col-sm-12 col-md-9 col-lg-6  mb-3'>
              <div className='shadow card w-100 mb-3'>
                <Details
                  currentInquiry={currentInquiry}
                  customerDetails={detailsItems}
                  description={description}
                  hideButton={hideButton(currentAction)}
                  inquiryDueoutDetails={inquiryDetailsItems}
                  previousPage={previousListPage}
                  roleContext={roleContext}
                  title='Inquiry Details'
                />
              </div>
              <div className='shadow card w-100'> 
                <Discussions
                  ItemGUID={guid}
                  allFiles={allFiles}
                  currentInquiry={currentInquiry}
                  hideButton={hideButton(currentAction)}
                  isInquiry={true}
                  isMenusExpanded={props?.isMenusExpanded}
                  refresh={refresh}
                  role={currentUserRole}
                  setIsLoading={setIsLoading}
                />
              </div>
            </div>
            <div className='col-sm-12 col-md-12 col-lg-3 '>
              <Checklist
                category={currentInquiry?.['Category/MetadataValue']}
                subCategory={currentInquiry?.['SubCategory/MetadataValue']}
              />
              <DetailViewFiles
                ItemGUID={guid}
                allFiles={allFiles}
                currentInquiry={currentInquiry}
                getSelectedFiles={getSelectedFiles}
                hideButton={hideButton(currentAction)}
                isInquiry={true}
                role={currentUserRole}
                setAllFiles={setAllFiles}
              />
              <TeamMembers
                currentInquiry={currentInquiry}
                hideButton={hideButton(currentAction)}
                isInquiry={true}
                refresh={refresh}
                role={currentUserRole}
                setRefresh={setRefresh}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
export default DetailView
